import { useParams } from 'react-router';
import { TagProps, TagValueProps } from 'spindl-shared-components';
import { DatabasePageParams } from '../../components/pages/DatabasePage/types';

import {
  CELL_DROPDOWN_X_OFFSET,
  CELL_DROPDOWN_Y_OFFSET,
  EXPANDED_CELL_DROPDOWN_Y_OFFSET,
  EXPANDED_MODAL_DROPDOWN_Y_OFFSET,
  SHOULD_EXPAND_MULTI_SELECT_AFTER,
} from '../constants';
import { getTranslation } from '../reactUtils';
import {
  DropDownItemCombinedProps,
  DropDownOption,
  MainCellProps,
  MainModalFieldProps,
} from '../types';
import { debounce, optionToValue } from '../utils';
import { MultiSelectFieldProps } from './types';
import { useSelectField } from './useSelectField';

export const useMultiSelectField = (mainProps: MainCellProps | MainModalFieldProps): MultiSelectFieldProps => {
  const { value, updateCellValue, dataType } = mainProps;
  const {
    options,
    selected,
    onDropdownToggle,
    setSelected,
    searchQuery,
    setSearchQuery,
    totalItemsFound,
    itemsDisplayed,
  } = useSelectField<string[]>(mainProps, []);

  const debouncedSetSearchQuery = debounce(setSearchQuery, 500);

  const { databaseSlug, tableSlug } = useParams<DatabasePageParams>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateSelectedOptions = async (option: string, metadata?: any) => {
    const optionsToSave = optionToValue(value, dataType, metadata);
    setSelected([...selected || [], option]);
    if (tableSlug && optionsToSave) {
      await updateCellValue?.(databaseSlug, tableSlug, optionsToSave);
    }
  };

  const deleteSelectedOption = async (option: string) => {
    const updatedOptions = selected.filter((displayOption) => {
      return displayOption !== option;
    });

    const optionsToUpdate = options?.filter(
      (dropdownItem: DropDownItemCombinedProps) => {
        return updatedOptions?.includes(dropdownItem.text?.value as string);
      },
    );

    setSelected(updatedOptions);

    const newCellValue = optionsToUpdate?.map((displayOption) => {
      const { value: optionValue } = displayOption.metadata;
      return optionValue as string;
    });

    if (newCellValue && tableSlug) {
      await updateCellValue?.(databaseSlug, tableSlug, newCellValue);
    }
  };

  const updateSelectedOption = async (
    event?: React.MouseEvent<HTMLElement>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata?: any,
  ) => {
    event?.preventDefault();
    event?.stopPropagation();
    const { text, value: optionValue } = metadata as DropDownOption;
    if (text && optionValue && !selected?.includes(text)) {
      await updateSelectedOptions(text, optionValue);
    }
  };

  const unselectOption = async (
    dropDownOption: string,
    event?: React.MouseEvent<HTMLElement>,
  ) => {
    event?.preventDefault();
    event?.stopPropagation();
    await deleteSelectedOption(dropDownOption);
  };

  const computeOffset = (): [number, number] => {
    const isCell = 'updateCellState' in mainProps;
    const yExpandedOffset = isCell
      ? EXPANDED_CELL_DROPDOWN_Y_OFFSET
      : EXPANDED_MODAL_DROPDOWN_Y_OFFSET;
    return (
      selected.length >= SHOULD_EXPAND_MULTI_SELECT_AFTER
        ? [CELL_DROPDOWN_X_OFFSET, yExpandedOffset]
        : [CELL_DROPDOWN_X_OFFSET, CELL_DROPDOWN_Y_OFFSET]
    );
  };

  const handleKeyDown = async (e?: React.KeyboardEvent<HTMLInputElement>) => {
    e?.stopPropagation();

    if (e?.key === 'Enter') {
      if (searchQuery && options?.length) {
        const firstOption = options[0];
        if (firstOption.metadata) {
          const { text, value: optionValue } = firstOption.metadata as DropDownOption;
          if (text && !selected.includes(text) && optionValue) {
            await updateSelectedOptions(text, optionValue);
          }
        }
      }
    }
  };

  const tags: (TagProps & TagValueProps)[] = selected.map((option) => {
    return {
      state: 'Selected',
      text: { value: option },
      button: {
        onMouseDown: (event?: React.MouseEvent<HTMLElement>) => unselectOption(option, event),
      },
    };
  }) || [];

  return {
    cellSelectDropDown: {
      toggleProps: {
        tagList: {
          tags: tags,
        },
      },
      menuProps: {
        onDropdownToggle,
        dropDownItems: options ? options.map((option) => ({
          ...option,
          onMouseDown: async (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
            await updateSelectedOption(event, option.metadata);
          },
          state: selected.includes(option.text?.value as string)
            ? 'Selected'
            : 'Unselected',
        })) : [],
        bottomBarText: {
          value: getTranslation('select.items_count', {
            itemsDisplayed,
            totalItemsFound,
          }),
        },
        type: dataType === 'multi-reference' ? 'Search' : 'Default',
        searchInput: {
          textValue: searchQuery,
          onTextChanged: (inputValue) => debouncedSetSearchQuery(inputValue),
          onKeyDown: handleKeyDown,
        },
      },
      offset: computeOffset(),
    },
    tagList: {
      tags: [{ ...tags[0], state: 'Default' }],
    },
    text: {
      value:
        (selected || []).length > 1
          ? `+${(selected || []).length - 1}`
          : '',
    },
  };
};
